import axios from "axios";
import React, { useEffect, useState } from "react";
import { useParams, useLocation, useHistory } from "react-router-dom";
import { ChatBox } from "../../components/ChatBox/ChatBox";
import { useSpreadSheet } from "../../context/spreadsheet-context";
import {
  listenerSingularCellSelection,
  listenerMultipleCellSelection,
  listenerEditedCellData,
  listenerLoadImport,
  // listenerCellStyle,
  listenerAddSheet,
  listenerOnChanged,
  listenerClients,
  emitRoomID,
  listenerClientArrOnDisconnect,
  listenAddMember
  // emitOnChanged
} from "../../socket";
import MouseContainer from "../mousecontainer";
import { useDocument } from "./documentInfoContex";
import { workwiseServices } from "../../Workw-Api/api";
import { formatFileSize, processTemplateData } from "../../utils/helper";
import { message } from "antd";
import Loader from "../../components/SkeletonLoader/Loader";
import ImageLayer from "../../components/SpreadsheetImages/ImageLayer";
import SpreadsheetImageUploadBridge from "../../components/SpreadsheetImages/SpreadsheetImageUploadBridge";
import { removeOfflineData, saveToLocalStorage } from "../../x-data-spreadsheet/src/utils/helper";
import { loadServerData, updateAllSheets, updateSheet, templateService } from "../../utils/api";
import { updateOrAppendSheet } from "../../x-data-spreadsheet/src/utils/helper";
import { notifySheetImagesMutated } from "../../utils/spreadsheetImageHelpers";
import { useDocumentCache } from "../../hooks/useDocumentCache";
import { isEffectivelyOffline } from "../../utils/helper";
import { getLocalGrid, updateLocalGridSheets, isLocalGridRoute } from "../../utils/localGrids";

export const SpreadsheetContainer = ({
  contentLoading,
  setContentLoading,
}) => {
  // const konn = "http://192.168.18.81:8080";
  const konn = "https://grid.workw.com";
  const { id } = useParams();
  const location = useLocation();
  const history = useHistory();
  const { setCurrPos } = useSpreadSheet();
  const {
    setSheets,
    setDocumentInfo,
    setSavingData,
    sheets,
    setIsExternalAllow,
    isReadOnly,
    setServerData,
    setIsConflictModalVisible,
    isTemplateDocument,
    setTemplateData,
    templateData,
    documentInfo
  } = useDocument();

  // Local (offline-created) grids live only in localStorage and have no server
  // counterpart, so they skip every fetch, save and socket room. Flagged by ?local=true.
  const isLocalGrid = React.useMemo(() => isLocalGridRoute(), []);

  // Document caching applies only to normal, server-backed documents.
  const isCacheEnabled = !isTemplateDocument && !isReadOnly && !isLocalGrid;
  const {
    cached,
    isUiLocked,
    lockUi,
    unlockUi,
    commitCache,
    cacheRenderedRef,
  } = useDocumentCache(id, isCacheEnabled);

  // True when a cached grid is available to paint instantly - suppresses the
  // loader from the very first render so no loader flashes before the paint.
  const hasRenderableCache =
    isCacheEnabled && Array.isArray(cached?.documentData?.spreadsheetData)
    && cached.documentData.spreadsheetData.length > 0;

  const {
    spreadSheet_init,
    spreadsheetInstanceRef,
    onCellSelected,
    onCellsSelected,
    onCellEdited,
    onCellStyle,
    onAddSheet,
    onDeleteSheet,
    onRenameSheet
  } = useSpreadSheet();

  const [isSpreadsheetReady, setIsSpreadsheetReady] = React.useState(false);
  const [spreadsheetData, setSpreadsheetData] = React.useState([]);
  /** Image insert uses `changeData` but sheet `change` is debounced (600ms); bump state so persist effect runs immediately. */
  const flushActiveSheetForPersist = React.useCallback(() => {
    const inst = spreadsheetInstanceRef.current;
    const sheetData = inst?.sheet?.data?.getData?.();
    if (!sheetData) return;
    setSpreadsheetData({
      ...sheetData,
      images: Array.isArray(sheetData.images) ? [...sheetData.images] : [],
    });
  }, [spreadsheetInstanceRef]);
  const [docExistsInDb, setIsDocSaved] = React.useState(false);
  const [serverVersionData, setServerVersionData] = useState(null);
  const [dataFetchSuccess, setDataFetchSuccess] = useState(false);
  const hasMounted = React.useRef(false);

  useEffect(() => {
    const fetchServerData = async () => {
      setContentLoading(true);
      try {
        // Check for templateId in query parameters
        const searchParams = new URLSearchParams(location.search);
        const templateId = searchParams.get('templateId');
        if (isTemplateDocument) {
          const response = await templateService.fetchTemplateById(id);
          const processed = processTemplateData(response);
          setTemplateData(processed);
          setDataFetchSuccess(true);
        } else if (templateId) {
          // If templateId is in URL, fetch template data
          const response = await templateService.fetchTemplateById(templateId);
          const processed = processTemplateData(response);
          // processed may contain spreadsheetData or mindmapData
          setServerData(processed);
          setServerVersionData(processed);
          setDataFetchSuccess(true);
          // Save template data to current document if it contains spreadsheetData
          if (processed?.spreadsheetData) {
            await updateAllSheets(id, processed.spreadsheetData);
          }
          // Remove templateId from URL
          const searchParams = new URLSearchParams(location.search);
          searchParams.delete('templateId');
          const newSearch = searchParams.toString();
          history.replace({
            pathname: location.pathname,
            search: newSearch ? `?${newSearch}` : ''
          });
        } else {
          const serverData = await loadServerData(id);
          setServerData(serverData);
          setServerVersionData(serverData);
          setDataFetchSuccess(true);
        }
      } catch (error) {
        console.error(`Error loading data for ${isTemplateDocument ? 'template' : 'sheet'} ${id}:`, error);
        message.error(`Failed to load ${isTemplateDocument ? 'template' : 'spreadsheet'} data`);
        setDataFetchSuccess(false);
      } finally {
        setContentLoading(false);
      }
    };

    if (isLocalGrid) {
      // No server document to fetch — initializeSpreadsheet reads localStorage.
      setContentLoading(false);
      return;
    }

    if (docExistsInDb || isTemplateDocument) {
      fetchServerData();
    }
  }, [id, docExistsInDb, isTemplateDocument, location.search, isLocalGrid]);

  useEffect(() => {

    const loadSpreadsheetData = (data, updateTime, isExternal) => {
      const finalData = data.filter(item => Object.keys(item).length > 0);
      const fileSize = formatFileSize(new TextEncoder().encode(JSON.stringify(finalData)).length);
      setIsExternalAllow(isExternal);
      setSavingData({ isSaving: '', lastSavedDateTime: '', fileSize });
      setSheets(finalData);
      spreadsheetInstanceRef.current.loadData(finalData, false);
      notifySheetImagesMutated();
      window.setTimeout(notifySheetImagesMutated, 150);
      const allSheetsData = spreadsheetInstanceRef.current.getData();
      const activeSheetData = allSheetsData[0] || null;
      if (activeSheetData && finalData.length === 0) {
        setSpreadsheetData(activeSheetData);
      }
      return finalData;
    };

    // Instant cached paint: render the last-known state immediately (UI locked)
    // without waiting for the background data API. Runs once, and intentionally
    // does NOT set isSpreadsheetReady so initializeSpreadsheet still reloads fresh
    // data over the preview when contentLoading resolves.
    const renderCachedInstant = async () => {
      if (!isCacheEnabled || cacheRenderedRef.current || isSpreadsheetReady) return;
      const cachedSheets = Array.isArray(cached?.documentData?.spreadsheetData)
        ? cached.documentData.spreadsheetData
        : [];
      if (cachedSheets.length === 0) return;
      try {
        await spreadSheet_init();
        if (!spreadsheetInstanceRef.current) return;
        loadSpreadsheetData(
          cachedSheets,
          cached?.documentData?.updatedAt || null,
          !!cached?.documentInfo?.isExternal
        );
        cacheRenderedRef.current = true;
        lockUi();
      } catch (error) {
        console.error('Cached instant render failed:', error);
      }
    };

    const initializeSpreadsheet = async () => {
      // Exit early if spreadsheet is already initialized
      if (isSpreadsheetReady) return;

      // Local grids load straight from localStorage — before the server-data
      // guard below, which they can never satisfy.
      if (isLocalGrid) {
        try {
          await spreadSheet_init();
          if (!spreadsheetInstanceRef.current) return;
          const record = getLocalGrid(id);
          const localSheets = Array.isArray(record?.sheets) ? record.sheets : [];
          loadSpreadsheetData(localSheets, record?.updateDate || null, false);
          setIsSpreadsheetReady(true);
        } catch (error) {
          console.error('Local grid initialization failed:', error);
          message.error('Failed to open this local grid');
        }
        return;
      }

      // Only proceed if not loading, data fetch was successful, and (serverVersionData exists or it's a template)
      if (contentLoading || !dataFetchSuccess || (!serverVersionData && !isTemplateDocument)) return;

      try {
        if (!isSpreadsheetReady) {
          await spreadSheet_init();
        }

        if (!spreadsheetInstanceRef.current) return;

        if (isTemplateDocument) {
          const sheetsData = Array.isArray(templateData?.spreadsheetData) ? templateData.spreadsheetData : [];
          loadSpreadsheetData(sheetsData, null, false);
          setIsSpreadsheetReady(true);
          return;
        }

        const offlineSheets = JSON.parse(localStorage.getItem('offlineSheets') || '{}');
        const localData = Array.isArray(offlineSheets[id]?.data) ? offlineSheets[id].data : [];
        const localUpdateTime = offlineSheets[id]?.lastUpdateTime || null;

        const serverSheetsData = Array.isArray(serverVersionData?.spreadsheetData)
          ? serverVersionData.spreadsheetData
          : [];
        const serverUpdateTime = serverVersionData?.updatedAt || null;

        const localDate = localUpdateTime ? new Date(localUpdateTime) : null;
        const serverDate = serverUpdateTime ? new Date(serverUpdateTime) : null;

        // Conflict case: both have data and server is newer
        if (localData && serverSheetsData && localDate && serverDate && (serverDate > localDate)) {
          loadSpreadsheetData(localData, localUpdateTime, !!offlineSheets[id]?.isExternal);
          setIsConflictModalVisible(true);
          setIsSpreadsheetReady(true);
          return;
        }

        // Both have data but local is newer 
        if (localData && serverSheetsData && localDate && serverDate && (serverDate < localDate)) {
          loadSpreadsheetData(localData, localUpdateTime, !!offlineSheets[id]?.isExternal);
          updateAllSheets(id, localData);
          removeOfflineData(id);
          setIsSpreadsheetReady(true);
          return;
        }

        // If server data exists load it 
        if (serverSheetsData) {
          loadSpreadsheetData(serverSheetsData, serverUpdateTime, !!serverVersionData?.isExternal);
          setIsSpreadsheetReady(true);
          return;
        }

        // No valid data
        message.error('Unable to load content. Data missing or unavailable.');

      } catch (error) {
        console.error('Spreadsheet initialization failed:', error);
        message.error('Failed to initialize spreadsheet');
      }
    };

    renderCachedInstant();
    initializeSpreadsheet();
  }, [contentLoading, serverVersionData, dataFetchSuccess, isSpreadsheetReady, spreadsheetInstanceRef, isTemplateDocument, templateData, cached, isCacheEnabled, lockUi, isLocalGrid, id]);

  // Unlock the UI once fresh data has loaded (isSpreadsheetReady flips true only
  // after initializeSpreadsheet swaps the cached preview for fresh server data).
  useEffect(() => {
    if (!isCacheEnabled) return;
    if (isSpreadsheetReady && isUiLocked) unlockUi();
  }, [isSpreadsheetReady, isUiLocked, isCacheEnabled, unlockUi]);

  // Mirror the latest user-visible state into the cache. Driven by in-memory
  // `sheets` so unsaved local edits always win over stale API data. Guarded by
  // isSpreadsheetReady so the locked cached preview is never written back.
  useEffect(() => {
    if (!isCacheEnabled || !isSpreadsheetReady) return;
    if (!Array.isArray(sheets) || sheets.length === 0) return;
    const documentData = {
      ...(serverVersionData || {}),
      spreadsheetData: sheets,
      updatedAt: new Date().toISOString(),
    };
    commitCache(documentData, documentInfo);
  }, [sheets, isSpreadsheetReady, documentInfo, serverVersionData, isCacheEnabled, commitCache]);

  // Setup all spreadsheet handlers after initialization
  React.useEffect(() => {
    if (isSpreadsheetReady && spreadsheetInstanceRef.current) {
      onCellSelected(id);
      onCellsSelected(id);
      onCellStyle();
      onAddSheet(id, setSavingData, isTemplateDocument);
      onDeleteSheet(id, setSavingData, isTemplateDocument);
      onRenameSheet(id, setSavingData, isTemplateDocument);

      // Setup change listener
      const changeHandler = (data) => {
        setSpreadsheetData(data);
      };
      spreadsheetInstanceRef.current.change(changeHandler);

      // Setup socket listeners
      listenerAddSheet(spreadsheetInstanceRef);
      listenerEditedCellData(spreadsheetInstanceRef);
      onCellEdited(spreadsheetInstanceRef);
      listenerSingularCellSelection(spreadsheetInstanceRef, 'COllab');
      listenerMultipleCellSelection(spreadsheetInstanceRef);
      listenerOnChanged(spreadsheetInstanceRef);
      listenerClients(spreadsheetInstanceRef);
      listenerClientArrOnDisconnect();
      listenerLoadImport(spreadsheetInstanceRef);

      listenAddMember(async () => {
        const response = await workwiseServices.getDocumentById(id);
        if (response && response?.status === 200 && response.data?.data?.rightType !== 0) {
          setDocumentInfo(response?.data?.data);
        }
      });
    }
  }, [isSpreadsheetReady, id]);
  React.useEffect(() => {
    // const sampleData = 
    // {
    //     "spreadsheetID": "xx",
    //     "spreadsheetData": [{
    //         "name": "Sheet 1",
    //         "freeze": "A1",
    //         "styles": [],
    //         "merges": [],
    //         "rows": {
    //             "len": 3000
    //         },
    //         "cols": {
    //             "len": 26
    //         },
    //         "validations": [],
    //         "autofilter": {}
    //     }],
    //     "readOnlySpreadsheet": "r.xx",
    //     "Comments": [],
    //     "__v": 1
    // };
    const createSpreadsheet = async () => {
      await axios.post(
        `${konn}/api/create`,
        {
          id: id,
          data: {},
        }
      );
      setIsDocSaved(true);
    };
    if (isLocalGrid) {
      // Nothing to create server-side; the record already exists in localStorage.
      return;
    }
    if (!isTemplateDocument) {
      createSpreadsheet();
    } else {
      setIsDocSaved(true); // For templates, assume it's "saved" since no creation needed
    }
  }, [id, isTemplateDocument, isLocalGrid]);

  // Update spreadsheet data
  useEffect(() => {
    if (!spreadsheetInstanceRef.current) return;

    // Skip the first mount to avoid unnecessary empty update
    if (!hasMounted.current) {
      hasMounted.current = true;
      return;
    }

    const updateSpreadsheet = async (data) => {
      // Manual "work offline" counts as offline here too, so edits buffer
      // locally instead of being pushed while the user asked us not to.
      const isOffline = isEffectivelyOffline();
      const updatedSheets = updateOrAppendSheet(data, sheets);
      setSheets(updatedSheets);

      // Local grids persist only to their own localStorage record — there is no
      // server document behind them and no offline buffer to reconcile.
      if (isLocalGrid) {
        updateLocalGridSheets(id, updatedSheets);
        return;
      }

      if (isOffline) {
        // Save to localStorage if offline
        saveToLocalStorage(id, updatedSheets);
        return;
      } else {
        // Save to server if online
        try {
          if (isTemplateDocument) {
            await templateService.updateTemplate({ templateId: id, data: { milepadData: JSON.stringify(updatedSheets), docType: 'milegrid' }, dispatch: setSavingData });
          } else {
            await axios.post(`${konn}/api/update`, {
              spreadsheetID: id,
              spreadsheetData: data,
            });
          }
          if (data.length !== 0) {
            workwiseServices.saveDocumentlastUpdate(id, setSavingData, sheets);
          }
          // Clear localStorage after successful sync
          removeOfflineData(id);
        } catch (error) {
          console.error("Update failed, saving to localStorage:", error);
          saveToLocalStorage(id, updatedSheets);
        }
      }
    };

    const timer = setTimeout(() => {
      updateSpreadsheet(spreadsheetData);
    }, 100);

    return () => clearTimeout(timer);
  }, [spreadsheetData, id]);


  return (
    // pointer-events:none must sit on the OUTER container: the x-data-spreadsheet
    // grid DOM is mounted directly into #spreadsheet-container, so locking only the
    // inner React wrapper would leave cells editable. `none` cascades to all
    // descendants (grid + children), fully blocking edits during the cached preview.
    <div
      id="spreadsheet-container"
      style={{ pointerEvents: isUiLocked ? 'none' : 'auto' }}
    >
      {(contentLoading || !isSpreadsheetReady) && !cacheRenderedRef.current && !hasRenderableCache ? (
        <Loader />
      ) : (
        <>
          <ChatBox />
          <MouseContainer />
          <ImageLayer
            spreadsheetInstanceRef={spreadsheetInstanceRef}
            isReadOnly={!!isReadOnly}
          />
          <SpreadsheetImageUploadBridge
            spreadsheetInstanceRef={spreadsheetInstanceRef}
            isReadOnly={!!isReadOnly}
            onAfterImageInserted={flushActiveSheetForPersist}
          />
        </>
      )}
    </div>
  );

};
